import type { ConfigSetting, MoneyJson } from '@/features/products/types/products.types';

/** The seven config-setting keys the SMRiQ plan-defaults section owns. */
export const SMRIQ_PLAN_KEYS = {
    durationDefault: 'smriq_duration_default',
    durationMin: 'smriq_duration_min',
    durationMax: 'smriq_duration_max',
    durationOptions: 'smriq_duration_options',
    motDefault: 'smriq_default_mot',
    allowMotSelection: 'allow_smriq_mot_selection',
    pricingVariant: 'smriq_pricing_variant',
} as const;

/** How duration is offered: platform defaults, a free-type min/max, a fixed list, or a
 *  single locked value (hidden in the quote journey). */
export type SmriqDurationMode = 'defaults' | 'type' | 'dropdown' | 'fixed';

export type SmriqPricingVariant = 'oem_approved' | 'division_approved';

export interface SmriqPlanDefaultsForm {
    duration_mode: SmriqDurationMode;
    duration_default: string;
    duration_min: string;
    duration_max: string;
    /** Comma-separated months, e.g. "12, 18, 24, 36". */
    duration_options: string;
    mot_default: '' | 'yes' | 'no';
    allow_mot_selection: boolean;
    pricing_variant: SmriqPricingVariant;
}

export type SettingValue = string | number | boolean | string[] | MoneyJson;

const asInt = (value: unknown): number | undefined => {
    if (Array.isArray(value)) return value.length === 1 ? asInt(value[0]) : undefined;
    if (typeof value === 'number') return Number.isInteger(value) ? value : undefined;
    if (typeof value === 'string' && /^-?\d+$/.test(value.trim())) return Number(value.trim());
    return undefined;
};

const asBool = (value: unknown): boolean | undefined => {
    if (Array.isArray(value)) return value.length === 1 ? asBool(value[0]) : undefined;
    if (typeof value === 'boolean') return value;
    if (value === 'true' || value === 1 || value === '1') return true;
    if (value === 'false' || value === 0 || value === '0') return false;
    return undefined;
};

const asIntList = (value: unknown): number[] | undefined => {
    if (!Array.isArray(value)) return undefined;
    const out: number[] = [];
    for (const element of value) {
        const parsed = asInt(element);
        if (parsed === undefined) return undefined;
        out.push(parsed);
    }
    return out;
};

const asPricingVariant = (value: unknown): SmriqPricingVariant => {
    const scalar = Array.isArray(value) ? (value.length === 1 ? value[0] : undefined) : value;
    return scalar === 'division_approved' ? 'division_approved' : 'oem_approved';
};

const valueFor = (settings: ConfigSetting[], key: string): unknown =>
    settings.find((setting) => setting.key === key)?.value;

const singleValueFor = (settings: ConfigSetting[], key: string): unknown => {
    const matches = settings.filter((setting) => setting.key === key);
    return matches.length === 1 ? matches[0].value : undefined;
};

/** Sorted, unique, positive months; null on a non-integer or non-positive entry, which
 *  SmriqPlanConfigResolver drops as a whole-list misconfiguration rather than saving. */
export const parseDurationOptions = (input: string): number[] | null => {
    const trimmed = input.trim();
    if (trimmed === '') return [];

    const parsed: number[] = [];
    for (const part of trimmed.split(',')) {
        const token = part.trim();
        if (!/^\d+$/.test(token)) return null;
        const months = Number(token);
        if (months < 1) return null;
        parsed.push(months);
    }
    return Array.from(new Set(parsed)).sort((a, b) => a - b);
};

export const readSmriqPlanDefaults = (settings: ConfigSetting[]): SmriqPlanDefaultsForm => {
    const durationDefault = asInt(valueFor(settings, SMRIQ_PLAN_KEYS.durationDefault));
    const durationMin = asInt(valueFor(settings, SMRIQ_PLAN_KEYS.durationMin));
    const durationMax = asInt(valueFor(settings, SMRIQ_PLAN_KEYS.durationMax));
    const options = asIntList(valueFor(settings, SMRIQ_PLAN_KEYS.durationOptions));
    const motDefault = asBool(valueFor(settings, SMRIQ_PLAN_KEYS.motDefault));
    const hasOptions = options != null && options.length > 0;

    return {
        // An options list is the dropdown, min === max a locked fixed duration, any other
        // bound free-type; nothing configured falls back to the platform defaults.
        duration_mode: hasOptions
            ? 'dropdown'
            : durationMin != null && durationMax != null && durationMin === durationMax
              ? 'fixed'
              : durationMin != null || durationMax != null || durationDefault != null
                ? 'type'
                : 'defaults',
        duration_default: durationDefault != null ? String(durationDefault) : '',
        duration_min: durationMin != null ? String(durationMin) : '',
        duration_max: durationMax != null ? String(durationMax) : '',
        duration_options: hasOptions ? options.join(', ') : '',
        mot_default: motDefault === true ? 'yes' : motDefault === false ? 'no' : '',
        // Unset means "allow" (the resolver's default), so the checkbox starts ticked.
        allow_mot_selection: asBool(valueFor(settings, SMRIQ_PLAN_KEYS.allowMotSelection)) !== false,
        pricing_variant: asPricingVariant(singleValueFor(settings, SMRIQ_PLAN_KEYS.pricingVariant)),
    };
};

const asOptionalInt = (raw: string): number | undefined =>
    raw.trim() === '' ? undefined : Number(raw.trim());

/** The desired value per key, `undefined` meaning the setting must be deleted. Allow-selection
 *  is stored only when false, since unset already means allow. */
export const buildSmriqPlanDefaults = (
    form: SmriqPlanDefaultsForm,
): Record<string, SettingValue | undefined> => {
    const options = parseDurationOptions(form.duration_options);
    const sharedKeys = {
        [SMRIQ_PLAN_KEYS.motDefault]: form.mot_default === '' ? undefined : form.mot_default === 'yes',
        [SMRIQ_PLAN_KEYS.allowMotSelection]: form.allow_mot_selection ? undefined : false,
        [SMRIQ_PLAN_KEYS.pricingVariant]:
            form.pricing_variant === 'division_approved' ? 'division_approved' : undefined,
    };

    if (form.duration_mode === 'defaults') {
        return {
            [SMRIQ_PLAN_KEYS.durationDefault]: undefined,
            [SMRIQ_PLAN_KEYS.durationMin]: undefined,
            [SMRIQ_PLAN_KEYS.durationMax]: undefined,
            [SMRIQ_PLAN_KEYS.durationOptions]: undefined,
            ...sharedKeys,
        };
    }

    if (form.duration_mode === 'dropdown') {
        return {
            [SMRIQ_PLAN_KEYS.durationDefault]: asOptionalInt(form.duration_default),
            [SMRIQ_PLAN_KEYS.durationMin]: undefined,
            [SMRIQ_PLAN_KEYS.durationMax]: undefined,
            [SMRIQ_PLAN_KEYS.durationOptions]:
                options && options.length > 0 ? options.map(String) : undefined,
            ...sharedKeys,
        };
    }

    if (form.duration_mode === 'fixed') {
        // A single locked value: min === max === default, so the range permits exactly it
        // and the quote journey hides the field.
        const fixed = asOptionalInt(form.duration_default);
        return {
            [SMRIQ_PLAN_KEYS.durationDefault]: fixed,
            [SMRIQ_PLAN_KEYS.durationMin]: fixed,
            [SMRIQ_PLAN_KEYS.durationMax]: fixed,
            [SMRIQ_PLAN_KEYS.durationOptions]: undefined,
            ...sharedKeys,
        };
    }

    return {
        [SMRIQ_PLAN_KEYS.durationDefault]: asOptionalInt(form.duration_default),
        [SMRIQ_PLAN_KEYS.durationMin]: asOptionalInt(form.duration_min),
        [SMRIQ_PLAN_KEYS.durationMax]: asOptionalInt(form.duration_max),
        [SMRIQ_PLAN_KEYS.durationOptions]: undefined,
        ...sharedKeys,
    };
};
